import { EvaluationResponse } from '@module/evaluation/model/EvaluationResponse';
import { ExpenseEvaluationResponse } from '@module/expense-evaluation/model/ExpenseEvaluationResponse';
import { createEntityAdapter, EntityState } from '@reduxjs/toolkit';
import { EvaluationDeletionReasonForm } from '@containers/common/da-tool/evaluation/DeleteEvaluationModal/DeleteEvaluationModal';
import { ProformaNewLocationDiffExpense } from '../../../types/ProformaPortExpenses';
import { mainRestApi } from '@state/stores/restApi/rest-api';
import { evaluationApi } from '../api';

const expenseEvaluationEntityAdapter =
  createEntityAdapter<ExpenseEvaluationResponse>({
    selectId: ({ id }) => id
  });

export const { selectById: selectExpenseEvaluationById } =
  expenseEvaluationEntityAdapter.getSelectors();

export const evaluationCoreApi = evaluationApi.injectEndpoints({
  endpoints: (build) => ({
    loadEvaluation: build.query<
      EvaluationResponse & {
        evaluationExpensesEntities: EntityState<ExpenseEvaluationResponse>;
      },
      { evaluationId: number }
    >({
      query: ({ evaluationId }) => ({
        url: `/${evaluationId}/load`,
        method: 'GET'
      }),

      transformResponse: (evaluationResponse: EvaluationResponse) => {
        // Helper function to filter deleted expenses and their nested expenses
        const filterDeletedExpenses = (
          expenses: ExpenseEvaluationResponse[]
        ): ExpenseEvaluationResponse[] => {
          return expenses
            .filter((expense) => !expense.isDeleted)
            .map((expense) => ({
              ...expense,
              editExpenses: (expense.editExpenses ?? []).filter(
                (e) => !e.isDeleted
              ),
              splitExpenses: (expense.splitExpenses ?? []).filter(
                (se) => !se.isDeleted
              )
            }));
        };

        // Filter deleted expenses from each section
        const filteredPortExpenses = filterDeletedExpenses(
          evaluationResponse.portExpensesSection?.expenseEvaluations ?? []
        );
        const filteredAgencyExpenses = filterDeletedExpenses(
          evaluationResponse.agencyExpensesSection?.expenseEvaluations ?? []
        );
        const filteredHusbandryExpenses = filterDeletedExpenses(
          evaluationResponse.husbandryExpensesSection?.expenseEvaluations ?? []
        );

        // Build entity state with all expenses (including editExpenses and splitExpenses)
        const expenseEvaluations: ExpenseEvaluationResponse[] = [
          ...filteredPortExpenses,
          ...filteredAgencyExpenses,
          ...filteredHusbandryExpenses,
          ...filteredAgencyExpenses.reduce<ExpenseEvaluationResponse[]>(
            (acc, cur) => {
              acc.push(
                ...(cur.editExpenses ?? []),
                ...(cur.splitExpenses ?? [])
              );
              return acc;
            },
            []
          ),
          ...filteredPortExpenses.reduce<ExpenseEvaluationResponse[]>(
            (acc, cur) => {
              acc.push(
                ...(cur.editExpenses ?? []),
                ...(cur.splitExpenses ?? [])
              );
              return acc;
            },
            []
          ),
          ...filteredHusbandryExpenses.reduce<ExpenseEvaluationResponse[]>(
            (acc, cur) => {
              acc.push(
                ...(cur.editExpenses ?? []),
                ...(cur.splitExpenses ?? [])
              );
              return acc;
            },
            []
          )
        ];

        const expenseEvaluationEntityState =
          expenseEvaluationEntityAdapter.setAll(
            expenseEvaluationEntityAdapter.getInitialState(),
            expenseEvaluations
          );

        return {
          ...evaluationResponse,
          // A draft evaluation comes back without an approval flow/signatures yet;
          // default them to arrays so the many consumers that iterate them
          // (ApprovalFlowTree, EvaluationPage, expense/discount sections…) don't crash.
          flow: evaluationResponse.flow ?? [],
          signatures: evaluationResponse.signatures ?? [],
          ...(evaluationResponse.portExpensesSection && {
            portExpensesSection: {
              ...evaluationResponse.portExpensesSection,
              expenseEvaluations: filteredPortExpenses
            }
          }),
          ...(evaluationResponse.agencyExpensesSection && {
            agencyExpensesSection: {
              ...evaluationResponse.agencyExpensesSection,
              expenseEvaluations: filteredAgencyExpenses
            }
          }),
          ...(evaluationResponse.husbandryExpensesSection && {
            husbandryExpensesSection: {
              ...evaluationResponse.husbandryExpensesSection,
              expenseEvaluations: filteredHusbandryExpenses
            }
          }),
          evaluationExpensesEntities: expenseEvaluationEntityState
        };
      },
      providesTags: (res, _err, { evaluationId }) =>
        res
          ? [{ type: 'EVALUATION', id: Number(evaluationId) }]
          : ['EVALUATION']
    }),

    markAsDeletedEvaluation: build.mutation<
      EvaluationResponse,
      EvaluationDeletionReasonForm
    >({
      query: (data) => ({
        url: `/mark-as-deleted`,
        data,
        method: 'POST'
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res
          ? [{ type: 'EVALUATION', id: Number(evaluationId) }]
          : ['EVALUATION']
    }),

    resetEvaluation: build.mutation<
      EvaluationResponse,
      { evaluationId: string | number; proformaGroupId: number | null }
    >({
      query: (data) => ({
        url: `/reset`,
        method: 'POST',
        data: data
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res
          ? [
              { type: 'EVALUATION', id: Number(evaluationId) },
              'BudgetStatus',
              'BudgetCommit'
            ]
          : ['EVALUATION'],
      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            mainRestApi.util.invalidateTags([
              { type: 'SECTION_PORT_EXPENSES', id: data.proforma.port.id }
            ])
          );
        } catch {
          // Only invalidate cross-API tags on success
        }
      }
    }),

    calculateNewLocationDiffsForEvaluation: build.query<
      ProformaNewLocationDiffExpense[],
      { evaluationId: number; terminalId: string; berthId?: string }
    >({
      query: ({ evaluationId, terminalId, berthId }) => ({
        url: `/${evaluationId}/calculate-new-location-diffs`,
        method: 'GET',
        params: {
          terminalId,
          berthId: berthId || undefined
        }
      })
    })
  }),
  overrideExisting: false
});

export const {
  useLoadEvaluationQuery,
  useMarkAsDeletedEvaluationMutation,
  useResetEvaluationMutation,
  useLazyCalculateNewLocationDiffsForEvaluationQuery
} = evaluationCoreApi;

export const invalidateEvaluation = (evaluationId: string | number) =>
  evaluationCoreApi.util.invalidateTags([
    { type: 'EVALUATION', id: Number(evaluationId) }
  ]);
